import React, { useEffect, useState } from "react";
import { Header } from "../../components";
import Layout from "../layout/Layout";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "../../firebase/firebase-config";
import { toast } from "react-toastify";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import BeatLoader from "react-spinners/BeatLoader";
import Tippy from "@tippyjs/react";
import { MdAdd, MdDelete, MdModeEditOutline } from "react-icons/md";
import Constant from "../../Constant";
import { DynamicTableCell, useTableContext } from "../../contexts/TableContext";
import Modal from "../../components/Modal";
import { fetchCountries } from "../../services/countries";
import {
  addTransactionConfig,
  deleteTransactionConfig,
  fetchTransactionConfigs,
  updateTransactionConfig,
} from "../../services/transaction-config";
import TX_TYPES from "../../constants/transaction-types";
import "./index.scss";

const tableHead = [
  { id: "1", label: "Country", minWidth: 120 },
  { id: "2", label: "Transaction type", minWidth: 140 },
  { id: "3", label: "Rate (%)", minWidth: 80 },
  { id: "4", label: "Action", minWidth: 100 },
];

const EMPTY_CREATE = { country: "", type: "", config_rate: "" };
const EMPTY_FILTERS = { type: "", country: "" };

const override = {
  textAlign: "center",
  marginTop: "20px",
};

const Setting = () => {
  const [user] = useAuthState(auth);
  const [authToken, setAuthToken] = useState("");
  const [configs, setConfigs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refresh, setRefresh] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE);
  const [editRate, setEditRate] = useState("");
  const [currentConfig, setCurrentConfig] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const { loading, setLoading } = useTableContext();

  const createRef = React.useRef(null);
  const editRef = React.useRef(null);
  const deleteRef = React.useRef(null);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const loadConfigs = (token) => {
    setLoading(true);
    fetchTransactionConfigs(token)
      .then((res) => {
        if (res.status_code === 200) {
          setConfigs(res.data?.configs || []);
        } else if (res.status_code === 404) {
          setConfigs([]);
        } else {
          toast.error(`${res.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error(`${err}`);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const loadCountries = (token) => {
    fetchCountries(token, { limit: 100 })
      .then((res) => {
        if (res.status_code === 200) {
          setCountries(res.data?.countries || []);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
      });
  };

  const openCreateModal = () => {
    setCreateForm(EMPTY_CREATE);
    createRef.current.open();
  };

  const handleCreate = () => {
    const rate = Number(createForm.config_rate);
    if (!createForm.country) {
      toast.error("Select a country");
      return;
    }
    if (!createForm.type) {
      toast.error("Select a transaction type");
      return;
    }
    if (!Number.isFinite(rate) || rate < 0) {
      toast.error("Rate must be a number greater than or equal to 0");
      return;
    }

    setLoading(true);
    addTransactionConfig(
      {
        country: createForm.country,
        type: createForm.type,
        config_rate: rate,
      },
      authToken
    )
      .then((result) => {
        if (result.status_code === 200) {
          toast.success(`${result.message}`);
          setRefresh((prev) => !prev);
          createRef.current.close();
        } else {
          toast.error(`${result.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error("Failed to add transaction rate");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdate = () => {
    const rate = Number(editRate);
    if (!currentConfig?.id) return;
    if (!Number.isFinite(rate) || rate < 0) {
      toast.error("Rate must be a number greater than or equal to 0");
      return;
    }

    setLoading(true);
    updateTransactionConfig(currentConfig.id, { config_rate: rate }, authToken)
      .then((result) => {
        if (result.status_code === 200) {
          toast.success(`${result.message}`);
          setRefresh((prev) => !prev);
          editRef.current.close();
        } else {
          toast.error(`${result.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error("Failed to update transaction rate");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDelete = () => {
    if (!toDelete?.id) return;
    setLoading(true);
    deleteTransactionConfig(toDelete.id, authToken)
      .then((result) => {
        if (result.status_code === 200) {
          toast.success(`${result.message}`);
          setRefresh((prev) => !prev);
        } else {
          toast.error(`${result.message}`);
        }
      })
      .catch((err) => {
        console.log("ERROR: ", err);
        toast.error("Failed to delete transaction rate");
      })
      .finally(() => {
        setLoading(false);
        deleteRef.current.close();
      });
  };

  useEffect(() => {
    if (!user) return;
    user.getIdToken(true).then((token) => {
      setAuthToken(token);
      loadConfigs(token);
      loadCountries(token);
    });
  }, [user, refresh]);

  const countryOptions = countries.filter(
    (item) => item.status === "ACTIVE" || item.name === "Nigeria"
  );

  const filterCountryOptions = Array.from(
    new Map(
      [
        ...countries,
        ...configs.map((row) => row.country).filter(Boolean),
      ]
        .filter((country) => country?.id)
        .map((country) => [country.id, country])
    ).values()
  ).sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const filteredConfigs = configs.filter((row) => {
    if (filters.type && row.type?.name !== filters.type) return false;
    if (filters.country && row.country?.id !== filters.country) return false;
    return true;
  });

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  return (
    <Layout>
      <div className="m-2 md:m-10 mt-24 p-2 md:p-5 bg-white rounded-3xl">
        <Header category="Settings" title="" />
        <div className="creation_sec">
          <button className="creartbtn" type="button" onClick={openCreateModal}>
            Add rate
            <MdAdd className="text-2xl" />
          </button>
        </div>
        <div className="txn-filters">
          <div>
            <label htmlFor="config-filter-country">Country</label>
            <select
              id="config-filter-country"
              value={filters.country}
              onChange={(e) => updateFilter("country", e.target.value)}
            >
              <option value="">All</option>
              {filterCountryOptions.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="config-filter-type">Transaction type</label>
            <select
              id="config-filter-type"
              value={filters.type}
              onChange={(e) => updateFilter("type", e.target.value)}
            >
              <option value="">All</option>
              {TX_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className="txn-filters-actions">
            <button
              type="button"
              className="txn-filter-clear"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>
        </div>
        <Paper className="main" sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer
            sx={{ maxHeight: 500, minWidth: 600 }}
            style={{ boxShadow: "none" }}
          >
            <h2 className="currency_h2">Transaction rates</h2>
            <Table stickyHeader aria-label="transaction rates">
              <TableHead>
                <TableRow>
                  {tableHead.map((column) => (
                    <TableCell
                      key={column.id}
                      align="center"
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConfigs
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => (
                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                      <DynamicTableCell>
                        <TableCell align="center">
                          {row.country?.name || "—"}
                        </TableCell>
                      </DynamicTableCell>
                      <DynamicTableCell>
                        <TableCell align="center">
                          {row.type?.name || "—"}
                        </TableCell>
                      </DynamicTableCell>
                      <DynamicTableCell>
                        <TableCell align="center">{row.config_rate}%</TableCell>
                      </DynamicTableCell>
                      <DynamicTableCell>
                        <TableCell align="center">
                          <Tippy content="Edit" animation="fade">
                            <button
                              onClick={() => {
                                setCurrentConfig(row);
                                setEditRate(
                                  row.config_rate === 0 || row.config_rate
                                    ? String(row.config_rate)
                                    : ""
                                );
                                editRef.current.open();
                              }}
                              className="bg-[#00A5EE] mx-1.5 rounded-full text-[#000] p-2"
                            >
                              <MdModeEditOutline size={16} color="#fff" />
                            </button>
                          </Tippy>
                          <Tippy content="Delete" animation="fade">
                            <button
                              onClick={() => {
                                setToDelete(row);
                                deleteRef.current.open();
                              }}
                              className="bg-[#E65B65] mx-1.5 rounded-full text-[#000] p-2"
                            >
                              <MdDelete size={16} color="#fff" />
                            </button>
                          </Tippy>
                        </TableCell>
                      </DynamicTableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <BeatLoader
            color="#36d7b7"
            loading={loading}
            cssOverride={override}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={filteredConfigs.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
        <Modal
          title="Add transaction rate"
          onSubmit={handleCreate}
          onClose={() => setCreateForm(EMPTY_CREATE)}
          ref={createRef}
        >
          <div className="edit_form">
            <div className="field">
              <label htmlFor="config-country">Country</label>
              <select
                id="config-country"
                className="__form-input"
                value={createForm.country}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, country: e.target.value }))
                }
              >
                <option value="">Select a country</option>
                {countryOptions.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="config-type">Transaction type</label>
              <select
                id="config-type"
                className="__form-input"
                value={createForm.type}
                onChange={(e) =>
                  setCreateForm((prev) => ({ ...prev, type: e.target.value }))
                }
              >
                <option value="">Select a type</option>
                {TX_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="config-rate">Rate (%)</label>
              <input
                id="config-rate"
                className="__form-input"
                type="number"
                min="0"
                step="0.01"
                value={createForm.config_rate}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    config_rate: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </Modal>
        <Modal
          title="Update transaction rate"
          onSubmit={handleUpdate}
          onClose={() => setCurrentConfig(null)}
          ref={editRef}
        >
          <div className="edit_form">
            <div className="field">
              <label>Country</label>
              <p className="readonly-value">{currentConfig?.country?.name || "—"}</p>
            </div>
            <div className="field">
              <label>Transaction type</label>
              <p className="readonly-value">{currentConfig?.type?.name || "—"}</p>
            </div>
            <div className="field">
              <label htmlFor="edit-config-rate">Rate (%)</label>
              <input
                id="edit-config-rate"
                className="__form-input"
                type="number"
                min="0"
                step="0.01"
                value={editRate}
                onChange={(e) => setEditRate(e.target.value)}
              />
            </div>
            <BeatLoader
              color={Constant.spinner_color}
              loading={loading}
              className="__center_spinner"
              cssOverride={{
                textAlign: "center",
                marginTop: "20px",
              }}
            />
          </div>
        </Modal>
        <Modal
          title={`Delete ${toDelete?.type?.name || "rate"}`}
          onSubmit={handleDelete}
          onClose={() => setToDelete(null)}
          ref={deleteRef}
        >
          <h2 className="text-center text-red-700 text-xl tracking-wide">
            Warning!!!
          </h2>
          <h2>
            Are you sure you want to delete the {toDelete?.type?.name} rate for{" "}
            {toDelete?.country?.name}?
          </h2>
        </Modal>
      </div>
    </Layout>
  );
};

export default Setting;
